import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable, map } from "rxjs";
import { ACCOUNTING_ENV } from "../../config/micros-env.tokens";
import { EnvMicroAccounting } from "../../config/env.micro.accounting";
import {
  BalanceAdjustment,
  BalanceAdjustmentCreateRequest,
  BalanceAdjustmentDecisionRequest,
  BalanceAdjustmentFilter,
  BalanceAdjustmentPage,
} from "../../../domain/models/accounting/balance-adjustments.model";
import { BalanceAdjustmentsPort } from "../../../app/ports/accounting/balance-adjustments.port";
import {
  isBalanceAdjustment,
  isBalanceAdjustmentPage,
} from "../../validators/accounting/balance-adjustments.validator";

const INVALID_PAGE = "La respuesta del listado de ajustes de saldo es inválida.";
const INVALID_ADJUSTMENT = "La respuesta del ajuste de saldo es inválida.";
const INVALID_VOUCHER = "La respuesta de la subida del comprobante es inválida.";
const VOUCHER_FOLDER = "26";
const HTTP_URL = /^https?:\/\/\S+$/i;
const ROLES_HEADER = "X-User-Roles";

@Injectable()
export class BalanceAdjustmentsHttpAdapter implements BalanceAdjustmentsPort {
  private static readonly PATH = "/api/v1/reconciliation-balance-adjustments";
  private static readonly DOCUMENT_STORAGE_PATH = "/ms-document-storage/api/v1/s3";

  constructor(
    private readonly http: HttpClient,
    @Inject(ACCOUNTING_ENV) private readonly env: EnvMicroAccounting,
  ) {}

  list(filter: BalanceAdjustmentFilter): Observable<BalanceAdjustmentPage> {
    let params = new HttpParams().set("limit", filter.limit).set("offset", filter.offset);
    if (filter.idCompany) params = params.set("idCompany", filter.idCompany);
    if (filter.date) params = params.set("date", filter.date);
    if (filter.search) params = params.set("search", filter.search);
    if (filter.status) params = params.set("status", filter.status);

    return this.http.get<unknown>(this.url(), { params }).pipe(
      map((response) => {
        if (!isBalanceAdjustmentPage(response)) throw new Error(INVALID_PAGE);
        return response;
      }),
    );
  }

  getById(idAdjustment: number): Observable<BalanceAdjustment> {
    return this.http.get<unknown>(this.url(`/${idAdjustment}`)).pipe(map(toAdjustment));
  }

  create(request: BalanceAdjustmentCreateRequest): Observable<BalanceAdjustment> {
    return this.http.post<unknown>(this.url(), request).pipe(map(toAdjustment));
  }

  approve(
    idAdjustment: number,
    request: BalanceAdjustmentDecisionRequest,
    roles: string,
  ): Observable<BalanceAdjustment> {
    return this.decide(idAdjustment, "approve", request, roles);
  }

  reject(
    idAdjustment: number,
    request: BalanceAdjustmentDecisionRequest,
    roles: string,
  ): Observable<BalanceAdjustment> {
    return this.decide(idAdjustment, "reject", request, roles);
  }

  cancel(idAdjustment: number, request: BalanceAdjustmentDecisionRequest): Observable<BalanceAdjustment> {
    return this.http.post<unknown>(this.url(`/${idAdjustment}/cancel`), request).pipe(map(toAdjustment));
  }

  uploadVoucher(file: File): Observable<string> {
    const body = new FormData();
    body.append("file", file);
    body.append("folderNumber", VOUCHER_FOLDER);
    return this.http.post<unknown>(this.documentStorageUrl(), body).pipe(map(toVoucherUrl));
  }

  deleteVoucher(voucherUrl: string): Observable<void> {
    const params = new HttpParams().set("fileUrl", voucherUrl);
    return this.http.delete<unknown>(this.documentStorageUrl(), { params }).pipe(map(() => undefined));
  }

  private decide(
    idAdjustment: number,
    action: "approve" | "reject",
    request: BalanceAdjustmentDecisionRequest,
    roles: string,
  ): Observable<BalanceAdjustment> {
    const headers = new HttpHeaders().set(ROLES_HEADER, roles);
    return this.http
      .post<unknown>(this.url(`/${idAdjustment}/${action}`), request, { headers })
      .pipe(map(toAdjustment));
  }

  private documentStorageUrl(): string {
    return `${this.env.host.replace(/\/$/, "")}${BalanceAdjustmentsHttpAdapter.DOCUMENT_STORAGE_PATH}`;
  }

  private url(suffix = ""): string {
    const baseUrl = `${this.env.host}${this.env.apiBasePath}`.replace(/\/$/, "");
    return `${baseUrl}${BalanceAdjustmentsHttpAdapter.PATH}${suffix}`;
  }
}

function toVoucherUrl(response: unknown): string {
  const url = typeof response === "object" && response !== null ? (response as Record<string, unknown>)["data"] : null;
  if (typeof url !== "string" || !HTTP_URL.test(url.trim())) throw new Error(INVALID_VOUCHER);
  return url.trim();
}

function toAdjustment(response: unknown): BalanceAdjustment {
  if (!isBalanceAdjustment(response)) throw new Error(INVALID_ADJUSTMENT);
  return response;
}
